import { Injectable } from '@angular/core';
import { MatCalendarCellClassFunction } from '@angular/material/datepicker';
import { Observable, Subject } from 'rxjs';
import {
  CalendarApi,
  DentalHolidayRuleCalendarDto,
} from './api-service/calendar/calendar-api';

export type DentalHolidayCode = '92133C' | '92094C';

export interface DentalHolidayRuleMessage {
  text: string;
  severity: 'extracted' | 'forbidden';
  conflicts?: DentalHolidayRuleConflict[];
}

export interface DentalHolidayRuleConflict {
  stdCode: string;
  regDate: string;
  source: 'grid';
}

type DateCodeMap = { [date: string]: string[] };
type VersionedCache = { [year: string]: DateCodeMap };
type LatestVersionMap = { [year: string]: string };

@Injectable({ providedIn: 'root' })
export class DentalHolidayCalendarCacheService {
  static readonly EffectiveDate = new Date(2026, 3, 1);

  private static readonly LatestVersionKey = 'DentalHolidayCalendar.latestByYear';
  private static readonly VersionKeyPrefix = 'DentalHolidayCalendar.';

  private readonly inFlight = new Map<number, Promise<boolean>>();
  private readonly memoryCache = new Map<number, DateCodeMap>();
  private readonly calendarYearUpdatedSubject = new Subject<number>();
  readonly calendarYearUpdated$: Observable<number> = this.calendarYearUpdatedSubject.asObservable();

  constructor(private calendarApi: CalendarApi) { }

  async ensureYear(year: number): Promise<boolean> {
    if (!Number.isInteger(year) || year < 1) {
      return false;
    }

    const existing = this.inFlight.get(year);
    if (existing) {
      return existing;
    }

    return this.startLoad(year, false);
  }

  async refreshYear(year: number): Promise<boolean> {
    if (!Number.isInteger(year) || year < 1) {
      return false;
    }

    while (this.inFlight.has(year)) {
      await this.inFlight.get(year);
    }

    return this.startLoad(year, true);
  }

  getDateCodes(date: Date | null | undefined): string[] | null {
    const dateValue = this.toDate(date);
    if (!dateValue) {
      return null;
    }

    const year = dateValue.getFullYear();
    const dateMap = this.getYearCache(year);
    if (!dateMap) {
      return null;
    }

    return dateMap[this.formatDate(dateValue)] || [];
  }

  getDateClass: MatCalendarCellClassFunction<any> = (date: any, view: any) => {
    if (view !== 'month') {
      return '';
    }
    const dateValue = this.toDate(date);
    const codes = this.getDateCodes(dateValue);
    if (!dateValue || !codes) {
      return '';
    }

    // 生效日前只有新版 92133C 的日期條件可套用給 92094C；92133C 本身不可用。
    const displayCodes = dateValue < DentalHolidayCalendarCacheService.EffectiveDate
      ? (codes.includes('92133C') ? ['92094C'] : [])
      : codes;
    const classes: string[] = [];
    if (displayCodes.includes('92133C')) {
      classes.push('dental-holiday-92133c');
    }
    if (displayCodes.includes('92094C')) {
      classes.push('dental-holiday-92094c');
    }
    return classes;
  };

  getRuleMessage(code: string, date: Date | null | undefined): DentalHolidayRuleMessage | null {
    const normalizedCode = (code || '').trim().toUpperCase();
    if (normalizedCode !== '92133C' && normalizedCode !== '92094C') {
      return null;
    }

    const dateValue = this.toDate(date);
    if (!dateValue) {
      return {
        text: `就診日無法判斷，${normalizedCode}不得申報`,
        severity: 'forbidden',
      };
    }

    // 2026-04-01 前：92133C 固定為 extracted；92094C 使用新版 92133C 條件。
    const codes = this.getDateCodes(dateValue);
    if (dateValue < DentalHolidayCalendarCacheService.EffectiveDate) {
      if (normalizedCode === '92133C') {
        return {
          text: '2026-04-01前不得申報92133C',
          severity: 'extracted',
          conflicts: [this.createVisitDateConflict(dateValue)],
        };
      }

      if (codes?.includes('92133C')) {
        return null;
      }

      return {
        text: codes === null
          ? '查無行政日曆快取，92094C不得申報'
          : '就診日不符合92133C假日申報日期，92094C不得申報',
        severity: 'forbidden',
        conflicts: [this.createVisitDateConflict(dateValue)],
      };
    }

    if (codes?.includes(normalizedCode)) {
      return null;
    }

    return {
      text: codes === null
        ? `查無行政日曆快取，${normalizedCode}不得申報`
        : `就診日不符合${normalizedCode}假日申報日期`,
      severity: 'forbidden',
      conflicts: [this.createVisitDateConflict(dateValue)],
    };
  }

  shouldHideFromDentalOrderHelper(code: string, date: Date | null | undefined): boolean {
    const dateValue = this.toDate(date);
    return (code || '').trim().toUpperCase() === '92133C'
      && !!dateValue
      && dateValue < DentalHolidayCalendarCacheService.EffectiveDate;
  }

  private startLoad(year: number, forceFull: boolean): Promise<boolean> {
    const request = this.loadYear(year, forceFull).finally(() => this.inFlight.delete(year));
    this.inFlight.set(year, request);
    return request;
  }

  private async loadYear(year: number, forceFull: boolean): Promise<boolean> {
    const latestVersions = this.readLatestVersions();
    const versionKey = latestVersions[String(year)];
    const cached = versionKey ? this.readVersion(versionKey, year) : null;
    const knownLastEditTime = !forceFull && versionKey && cached ? versionKey : undefined;

    let result: DentalHolidayRuleCalendarDto;
    try {
      result = await this.calendarApi.GetDentalHolidayRuleCalendarIfChanged(year, knownLastEditTime);
    } catch {
      if (forceFull) {
        return false;
      }
      return !!this.getYearCache(year);
    }

    if (result?.IsUnchanged) {
      if (!cached) {
        return false;
      }
      this.memoryCache.set(year, cached);
      return true;
    }

    const resultVersion = this.normalizeTimestamp(result?.LastEditTime);
    const dateMap = result?.Dates || {};
    if (resultVersion) {
      this.writeVersion(resultVersion, year, dateMap);
      latestVersions[String(year)] = resultVersion;
    } else {
      delete latestVersions[String(year)];
    }
    this.writeLatestVersions(latestVersions);
    this.memoryCache.set(year, dateMap);
    if (forceFull) {
      this.calendarYearUpdatedSubject.next(year);
    }
    return true;
  }

  private getYearCache(year: number): DateCodeMap | null {
    const inMemory = this.memoryCache.get(year);
    if (inMemory) {
      return inMemory;
    }

    const latestVersion = this.readLatestVersions()[String(year)];
    if (!latestVersion) {
      return null;
    }

    const cached = this.readVersion(latestVersion, year);
    if (cached) {
      this.memoryCache.set(year, cached);
    }
    return cached;
  }

  private readLatestVersions(): LatestVersionMap {
    try {
      return JSON.parse(localStorage.getItem(DentalHolidayCalendarCacheService.LatestVersionKey) || '{}') || {};
    } catch {
      return {};
    }
  }

  private writeLatestVersions(value: LatestVersionMap): void {
    try {
      localStorage.setItem(DentalHolidayCalendarCacheService.LatestVersionKey, JSON.stringify(value));
    } catch {
      // localStorage 不可用時不阻斷掛號或病歷流程。
    }
  }

  private readVersion(version: string, year: number): DateCodeMap | null {
    try {
      const payload = JSON.parse(localStorage.getItem(this.getVersionKey(version)) || '{}') as VersionedCache;
      return payload?.[String(year)] || null;
    } catch {
      return null;
    }
  }

  private writeVersion(version: string, year: number, dateMap: DateCodeMap): void {
    try {
      const key = this.getVersionKey(version);
      const payload = JSON.parse(localStorage.getItem(key) || '{}') as VersionedCache;
      payload[String(year)] = dateMap;
      localStorage.setItem(key, JSON.stringify(payload));
    } catch {
      // localStorage 不可用時不阻斷掛號或病歷流程。
    }
  }

  private getVersionKey(version: string): string {
    return DentalHolidayCalendarCacheService.VersionKeyPrefix + version;
  }

  private normalizeTimestamp(value: Date | string | null | undefined): string | null {
    if (!value) {
      return null;
    }

    const timestamp = value instanceof Date ? value.toISOString() : value;
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? null : timestamp;
  }

  private toDate(value: any): Date | null {
    if (!value) {
      return null;
    }

    const date = value instanceof Date
      ? new Date(value.getTime())
      : (typeof value?.toDate === 'function' ? value.toDate() : new Date(value));
    return isNaN(date.getTime()) ? null : date;
  }

  private formatDate(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  private createVisitDateConflict(date: Date): DentalHolidayRuleConflict {
    return {
      stdCode: '',
      regDate: `${date.getFullYear() - 1911}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`,
      source: 'grid',
    };
  }
}
